import {Base64, omit, difference, keys, keyBy, createReducer, nodupes, map, filter, chain, moment, groupBy} from 'helpers';
import {RootState, Actions} from 'store/types';
import * as actions from '../actions/goals';
import {ServerActions} from 'store/server';
import {Goal} from 'types';

export const index = createReducer<RootState['goals']['index'], Actions<typeof actions> | ServerActions>({}, {
  GOALS_UPDATE: (state, {goal}) => {
    return {
      ...state,
      [goal.id]: {
        //default values
        progressType: 'cumulative',
        status: 'In Progress',
        //values to update
        ...state[goal.id],
        ...goal
      }
    }
  },

  GOALS_DUPLICATE: (state, action) => {
    const {name = ''} = state[action.targetId] ?? {};
    const goal = {
      ...state[action.targetId],
      id: action.duplicateId,
      status: 'In Progress',
    };
    if (action.targetId !== action.duplicateId) {
      goal.name = nodupes({names: {[name]: 1}})(name);
    }
    if (action.milestones.length) {
      const milestones = action.milestones.map(milestone => ({
        ...milestone,
        parent_id: goal.id,
        id: action.generateId('milestone' + milestone.id),
        status: 'In Progress',
        isTemplate: 0,
      }));
      state = {
        ...state,
        ...keyBy(milestones, 'id'),
      }
    }
    return {
      ...state,
      [goal.id]: goal
    }
  },

  GOALS_HOLD: (state, {id}) => {
    return {
      ...state,
      [id]: {
        ...state[id],
        status: state[id]?.status == 'On Hold' ? 'In Progress' : 'On Hold',
      }
    }
  },

  GOALS_TOGGLE: (state, {id}) => {
    return {
      ...state,
      [id]: {
        ...state[id],
        status: state[id]?.status == 'Completed' ? 'In Progress' : 'Completed',
      }
    }
  },

  GOALS_ATTACH_MILESTONE: (state, {source_id, goal_id}) => {
    if (source_id) {
      return {
        ...state,
        [source_id]: {
          ...state[source_id],
          parent_id: goal_id,
        }
      }
    }
    return state;
  },

  GOALS_ATTACH_TO_PROJECT: (state, {source_id, target_id}) => {
    if (source_id && state[source_id]) {
      return {
        ...state,
        [source_id]: {
          ...state[source_id],
          typeId: target_id
        }
      }
    }
    return state;
  },

  GOALS_DELETE: (state, action) => {
    return omit(state, [
      action.id,
      ...map(action.milestones, milestone => milestone.id),
    ]);
  },

  GOALS_DELETE_PROJECT: (state, action) => {
    const matched = filter(state, {typeId: action.id});
    if (matched.length) {
      return {
        ...state,
        ...chain(matched)
          .map(goal => ({
            ...goal,
            typeId: 0
          }))
          .keyBy(goal => goal.id)
          .value()
      }
    }
    return state;
  },

  SERVER_DATA_RECEIVED: (state, action) => {
    const removed = difference(keys(state), action.existingElementIds.goals);
    const goals = action.goals
      .map((goal): Goal => ({
        category: (Base64.decode(goal.type) || 'other').replace(/ \d+$/, '') as Goal['category'],
        dodifferently: Base64.decode(goal.dodifferently),
        gain: Base64.decode(goal.gain),
        holdingback: Base64.decode(goal.holdingback),
        id: goal.serverId,
        importance: Base64.decode(goal.importance),
        isMajor: parseInt(goal.isMajor),
        isSmart: goal.isSmart,
        isTemplate: parseInt(goal.isTemplate),
        localMark: goal.localMark,
        name: Base64.decode(goal.name),
        note: Base64.decode(goal.note),
        obstacles: Base64.decode(goal.obstacle1),
        parent_id: parseInt(goal.parentGoalId) > 0 ? goal.parentGoalId : undefined,
        price: Base64.decode(goal.price1),
        progressType: goal.targetType || 'cumulative',
        risks: Base64.decode(goal.risk1),
        starting_date: goal.startingDate && moment(goal.startingDate, 'MM/DD/YYYY').startOf('day').unix(),
        starting_value: parseFloat(goal.startingPoint) || '',
        status: goal.status || 'In Progress',
        target_date: goal.achieveDate && moment(goal.achieveDate, 'MM/DD/YYYY').startOf('day').unix(),
        target_value: parseFloat(goal.quantitativeTarget) || '',
        typeId: goal.goalTypeId,
        unit: Base64.decode(goal.unit),
        unitId: goal.unitId,
        isWeeklyObjective: Base64.decode(goal.type) === 'weekly_objective',
      }))
      .filter(goal => {
        if (!goal.isWeeklyObjective && !goal.parent_id && (goal.isMajor === 0 || !goal.starting_date) || goal.isTemplate) {
          return false;
        }
        return true;
      });
    if (goals.length || removed.length) {
      return omit({
        ...state,
        ...keyBy(goals, 'id'),
      }, removed);
    }
    return state;
  },

  SERVER_DATA_RECEIVED_AFTER: (state) => state,

  TESTING_DELETE_ALL: () => ({}),
});
